import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  getPartnersThunk,
  selectPartners,
  setCurrentPartner,
} from "../../partnersSlice";
import { Button, Dropdown, Input, Menu, Space, Table, Tag } from "antd";
import type { TablePaginationConfig } from "antd/es/table";
import {
  EditOutlined,
  EuroOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import styles from "./PartnersTable.module.css";
import {
  selectIdToken,
  selectIsKlearlyAdmin,
  selectUser,
} from "../../../access-control/accountSlice";
import { PARTNER_HUB } from "../../../../config";
import {
  IPartner,
  PartnerType,
} from "../../../../interfaces/partner.interface";
import PartnerWizard from "../partner-wizard/PartnerWizard";
import CreatePartnerReportFormModal from "../create-partner-report-form/CreatePartnerReportForm";
import PartnerTeamMemberModal from "../partner-team-member-modal/PartnerTeamMemberModal";
import CountryFlag from "../../../common/country-flag/CountryFlag";
import { KlearlyCountryAlpha2Code } from "../../../../interfaces/klearlyCountryAlpha2Code.enum";
import { Select } from "antd";
import debounce from "lodash/debounce";
import {
  selectPartnersCurrentPage,
  selectPartnersPageSize,
  selectTotalPartnersCount,
} from "../../partnersSlice";

export default function PartnersTable() {
  const [loading, setLoading] = useState(false);
  const partners = useAppSelector(selectPartners);
  const dispatch = useAppDispatch();
  const idToken = useAppSelector(selectIdToken);
  const isKlearlyAdmin = useAppSelector(selectIsKlearlyAdmin);
  const user = useAppSelector(selectUser);
  const canAccessPartnerHub = isKlearlyAdmin;
  const totalCount = useAppSelector(selectTotalPartnersCount);
  const initialCurrent = useAppSelector(selectPartnersCurrentPage) || 1;
  const initialPageSize = useAppSelector(selectPartnersPageSize) || 10;
  const [tableParams, setTableParams] = useState<{
    pagination: TablePaginationConfig;
  }>({
    pagination: { current: initialCurrent, pageSize: initialPageSize },
  });
  const [filters, setFilters] = useState<
    Partial<IPartner & { name: string; country: string }>
  >({});

  const [editWizardVisible, setEditWizardVisible] = useState(false);
  const [createPartnerReportFormVisible, setCreatePartnerReportFormVisible] =
    useState(false);
  const [partnerTeamMemberModalVisible, setPartnerTeamMemberModalVisible] =
    useState(false);
  useEffect(() => {
    getPartners(filters, tableParams);
  }, [JSON.stringify(filters), JSON.stringify(tableParams)]);

  async function getPartners(filters?: Partial<IPartner>, tableParams?: any) {
    setLoading(true);
    const currentPage = tableParams?.pagination?.current || 1;
    const pageSize = tableParams?.pagination?.pageSize || 10;
    await dispatch(
      getPartnersThunk({
        ...filters,
        currentPage,
        pageSize,
      })
    );
    setLoading(false);
  }

  function handleEditPartner(partnerId: string) {
    dispatch(setCurrentPartner(partnerId));
    setEditWizardVisible(true);
  }
  function handleCreatePartnerReport(partnerId: string) {
    dispatch(setCurrentPartner(partnerId));
    setCreatePartnerReportFormVisible(true);
  }
  function handlePartnerTeamMemberModal(partnerId: string) {
    dispatch(setCurrentPartner(partnerId));
    setPartnerTeamMemberModalVisible(true);
  }

  const openPartnerHub = (partner: IPartner) => {
    if (!idToken) return;
    const url = new URL(PARTNER_HUB.BASE_URL);
    url.searchParams.set("adminIdToken", idToken);
    url.searchParams.set("partnerId", partner._id);
    if (user?.email) url.searchParams.set("adminEmail", user.email);
    if (user?.name) url.searchParams.set("adminName", user.name);
    url.searchParams.set("source", "klearly-admin");
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  function getPartnerTypeColor(partnerType?: PartnerType) {
    switch (partnerType) {
      case PartnerType.SoftwareVendor:
        return "green";
      case PartnerType.Reseller:
        return "blue";
      case PartnerType.FullServicePartner:
        return "purple";
      case PartnerType.FullServiceWithResellers:
        return "gold";
      default:
        return "";
    }
  }
  function getPartnerTypeLabel(partnerType?: PartnerType) {
    switch (partnerType) {
      case PartnerType.SoftwareVendor:
        return "Software Vendor";
      case PartnerType.Reseller:
        return "Reseller";
      case PartnerType.FullServicePartner:
        return "Full Service Partner";
      case PartnerType.FullServiceWithResellers:
        return "Full Service With Resellers";
      default:
        return "No Partner Type";
    }
  }
  const getCountryName = (alpha2?: KlearlyCountryAlpha2Code) => {
    if (!alpha2) return "";
    const entry = Object.entries(KlearlyCountryAlpha2Code).find(
      ([, code]) => code === alpha2
    );
    return entry ? entry[0] : (alpha2 as string).toUpperCase();
  };
  const handleTypeFilterChange = (value: PartnerType | "all") => {
    setFilters((prev) => {
      if (value === "all") {
        //@ts-ignore
        delete prev.partnerType;
        return { ...prev };
      } else return { ...prev, partnerType: value };
    });
    setTableParams((prev) => ({
      pagination: { ...prev.pagination, current: 1 },
    }));
  };
  const handleCountryFilterChange = (
    value: KlearlyCountryAlpha2Code | "all"
  ) => {
    setFilters((prev) => {
      if (value === "all") {
        //@ts-ignore
        delete prev.country;
        return { ...prev };
      } else return { ...prev, country: value };
    });
    setTableParams((prev) => ({
      pagination: { ...prev.pagination, current: 1 },
    }));
  };

  const debouncedNameFilterChange = useCallback(
    debounce((nextValue) => handleNameFilterChange(nextValue), 400),
    []
  );
  const handleDebouncedNameFilterChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    debouncedNameFilterChange(event);
  };
  const handleNameFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value.toLowerCase();
    setFilters((prev) => {
      if (!name || name === "") {
        //@ts-ignore
        delete prev.name;
        return { ...prev };
      } else {
        return { ...prev, name: name.replace(/\s+/g, " ").trim() };
      }
    });
    setTableParams((prev) => ({
      pagination: { ...prev.pagination, current: 1 },
    }));
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableParams({ pagination });
  };
  return (
    <div>
      <Space size="middle" style={{ marginBottom: 12 }}>
        <Select
          placeholder="Partner Type"
          allowClear
          style={{ width: 220 }}
          onChange={(v: any) => handleTypeFilterChange(v || "all")}
          options={[
            { label: "All", value: "all" },
            {
              label: getPartnerTypeLabel(PartnerType.SoftwareVendor),
              value: PartnerType.SoftwareVendor,
            },
            {
              label: getPartnerTypeLabel(PartnerType.Reseller),
              value: PartnerType.Reseller,
            },
            {
              label: getPartnerTypeLabel(PartnerType.FullServicePartner),
              value: PartnerType.FullServicePartner,
            },
            {
              label: getPartnerTypeLabel(PartnerType.FullServiceWithResellers),
              value: PartnerType.FullServiceWithResellers,
            },
          ]}
        />
        <Select
          placeholder="Country"
          allowClear
          showSearch
          optionFilterProp="children"
          style={{ width: 220 }}
          onChange={(v: any) => handleCountryFilterChange(v || "all")}
        >
          <Select.Option value="all">All</Select.Option>
          {Object.entries(KlearlyCountryAlpha2Code).map(([name, code]) => (
            <Select.Option key={code} value={code}>
              <CountryFlag alpha2={code} style={{ marginRight: 6 }} />
              {name}
            </Select.Option>
          ))}
        </Select>
      </Space>
      <div className={styles.table_header}>
        <Input.Search
          placeholder="Search by name"
          allowClear
          onChange={handleDebouncedNameFilterChange}
          style={{ width: "100%" }}
        />
      </div>
      <Table
        dataSource={partners?.map((p) => ({ ...p, key: p._id }))}
        pagination={{
          current: tableParams.pagination.current,
          pageSize: tableParams.pagination.pageSize,
          total: totalCount,
        }}
        onChange={handleTableChange}
        loading={loading}
        size="middle"
        scroll={{ x: "max-content" }}
      >
        <Table.Column
          title="Partner"
          key="name"
          render={(_: any, item: IPartner) => (
            <>
              <CountryFlag
                alpha2={
                  (item.country as KlearlyCountryAlpha2Code) ||
                  KlearlyCountryAlpha2Code.Netherlands
                }
                style={{ marginRight: 6 }}
              />
              {item.name}
            </>
          )}
        />
        <Table.Column
          title="Email"
          key="email"
          render={(_: any, i: IPartner) => <span>{i.email}</span>}
        />
        <Table.Column
          title="Phone"
          key="phone"
          render={(_: any, i: IPartner) => (
            <span>{`+${i.phone?.countryCode} ${i.phone?.number}`}</span>
          )}
        />
        <Table.Column
          title="Type"
          key="type"
          render={(_: any, i: IPartner) => (
            <Tag
              color={getPartnerTypeColor(i?.partnerType)}
              icon={
                !i?.partnerType ? (
                  <ExclamationCircleOutlined style={{ color: "red" }} />
                ) : null
              }
            >
              {getPartnerTypeLabel(i?.partnerType) || "No Partner Type"}
            </Tag>
          )}
        />
        <Table.Column
          title="Country"
          key="country"
          render={(_: any, i: IPartner) => (
            <span>
              <CountryFlag
                alpha2={
                  (i.country as KlearlyCountryAlpha2Code) ||
                  KlearlyCountryAlpha2Code.Netherlands
                }
                style={{ marginRight: 6 }}
              />
              {getCountryName(
                (i.country as KlearlyCountryAlpha2Code) ||
                  KlearlyCountryAlpha2Code.Netherlands
              )}
            </span>
          )}
        />
        <Table.Column
          title="Actions"
          key="action"
          render={(_: any, item: IPartner) => (
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    onClick={() => {
                      handleEditPartner(item._id);
                    }}
                    icon={<EditOutlined />}
                  >
                    Edit
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => {
                      handleCreatePartnerReport(item._id);
                    }}
                    icon={<EuroOutlined />}
                  >
                    Markup Report
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => {
                      handlePartnerTeamMemberModal(item._id);
                    }}
                    icon={<TeamOutlined />}
                  >
                    Team Members
                  </Menu.Item>
                  {canAccessPartnerHub && idToken && (
                    <Menu.Item
                      key="partner-hub"
                      onClick={() => {
                        openPartnerHub(item);
                      }}
                      icon={<ExportOutlined />}
                    >
                      Partner Hub
                    </Menu.Item>
                  )}
                </Menu>
              }
              placement="bottomLeft"
            >
              <Button type="default">Actions</Button>
            </Dropdown>
          )}
        />
      </Table>

      {/* Loading is handled by the Table component */}
      {editWizardVisible && (
        <PartnerWizard
          mode="edit"
          visible={editWizardVisible}
          setVisible={setEditWizardVisible}
        />
      )}
      {createPartnerReportFormVisible && (
        <CreatePartnerReportFormModal
          visible={createPartnerReportFormVisible}
          setVisible={setCreatePartnerReportFormVisible}
        />
      )}
      {partnerTeamMemberModalVisible && (
        <PartnerTeamMemberModal
          visible={partnerTeamMemberModalVisible}
          setVisible={setPartnerTeamMemberModalVisible}
        />
      )}
    </div>
  );
}
